export const SECTION_NAME = "SerialNumber";

export const INTRO_TEXT = `Serialized inventory allows you to track individual
  units of an item, rather than quantities only. A serial number is assigned
  to each unit, and the number stays with the unit as it goes through the
  system.`;

export const EXAMPLE_JSON = `{ 
  "id":123,
  "syncToken":0,
  "starred":0,
  "number":"988CP2382019",
  "item": { 
    "id": 64,
    "name": 'Widget, 1/4"'
  },
  "location": { 
    "id": 49,
    "name": 'Warehouse 17'
  },
  "customer": { 
    "id": 387,
    "name": 'ABC Plastics'
  },
  "warranty": {
    "start": ""2019-05-08T09:13:00"",
    "end": ""2020-05-08T09:13:00"",
    "warranty": {
      "id": 1,
      "name": "1 Year Limited"
    }
  },
  "description":"",
  "status": "Used",
  "cost": 50.0,
  "net": -50.0,
  "hasImage": false,
  "summaryOnly": false,
  "imageAsBase64String": null,
  "imageChanged": false,
  "customFields":[ 
    { 
      "id":5,
      "name":"Checkbox",
      "value":"false",
      "dataType":"Boolean"
    }
  ],
}`;

export const ATTRIBUTES = [
  {
    name: "id",
    type: "integer",
    description:
      "Unique identifier for this record. Must not be provided on create transactions.",
  },
  {
    name: "starred",
    type: "integer",
    description: `Indicates if this transaction has been starred. A value of 0 = no star; 1 or 1-3 = starred. Star colors 
      depend on application configuration. This could be one color of star or three colors of stars. See Company Settings in the user guide for more details.`,
  },
  {
    name: "syncToken",
    type: "integer",
    description: `Indicates the current version of this record. If you receive an error when updating a record, it is because your syncToken is for an older version of the record than that which is currently in the database. Please GET the latest version prior to updating.`,
  },
  {
    name: "number",
    type: "string",
    description: `The serial identification number.`,
  },
  {
    name: "item",
    type: "reference",
    description: "The item associated with this serial number.",
  },
  {
    name: "location",
    type: "reference",
    description: "The location for this serial number.",
  },
  {
    name: "customer",
    type: "reference",
    description: "The customer associated with this serial number.",
  },
  {
    name: "warranty",
    type: "warranty information",
    description: "Self-explanatory.",
  },
  {
    name: "description",
    type: "string",
    description: `Default description for this serial number.`,
  },
  {
    name: "status",
    type: "string",
    description: `The status of this serial number: "In Stock", "Used", "Shipped", or "Adjusted Out".`,
  },
  {
    name: "cost",
    type: "decimal",
    description: `Cost of this serialized item.`,
    readOnly: true,
  },
  {
    name: "net",
    type: "decimal",
    description: `Net cost of this serialized item.`,
    readOnly: true,
  },
  {
    name: "hasImage",
    type: "boolean",
    description: `True if a product image is available for this serialized item. False, if not.`,
  },
  {
    name: "imageAsBase64String",
    type: "string",
    description: `Binary image of this item, encoded as a Base64 string.`,
  },
  {
    name: "imageChanged",
    type: "boolean",
    description: `True if a new item image is being sent with this create or update transaction. False, if not.`,
  },
  {
    name: "customFields",
    type: "array of custom fields",
    description: `The list of custom fields for this object type.`,
  },
];

export const LINE_ITEM = [];

export const QUERY_PARAMS = [
  {
    name: "start",
    type: "number",
    description: `A cursor used in pagination.  This is the row number of the full set of results. 
     The API limits results to a max of 200 results per call.  If you want to retrieve the next set
      of results you can use this parameter to retrive the next set of results.  For example if you
       are retrieving 200 results at a time, you can set start=201 to retrieve the next page of results.`,
  },
  {
    name: "maxresults",
    type: "number",
    description: `The maximum number of results you want to return.  The default is 200, the maximum value allowed.`,
  },
  {
    name: "query",
    type: "string",
    description: `This parameter will filter the results by matches of the string on the following fields: number, description, or item name.`,
  },
  {
    name: "location",
    type: "string",
    description: `The name of a location to filter the items.`,
  },
  {
    name: "itemId",
    type: "number",
    description: `The id of the item to filter the items.`,
  },
  {
    name: "status",
    type: "string: instock, used, shipped, adjustedout",
    description: `Filters results by the status of the serial item.`,
  },
  {
    name: "createdsince/updatedsince",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS ",
    description: `Filters transactions created or updated since a specified date/time.`,
  },
];

export const SAMPLE_QUERY_REQUEST =
  "/api/v2/serial?start=101&maxresults=100&location=Warehouse%201&createdsince=2019-09-10T00:00:00&updatedsince=2019-09-10T23:59:59";
